import { useLazyQuery } from "@apollo/client";
import { MantineJasperThemeProvider } from "@jasper/forms";
import {
  buildProductStepPath,
  type ConfigurableProduct,
  getCompletedSteps,
  getItemTypeForProductType,
  getStepsForProduct,
  type OrderConfiguration,
  StepNavigation,
  useBulkProductGroup,
  useProductWorkflow,
  useStepSaveCoordinator,
} from "@jasper/product-workflows";
import { GetOrderByUniqueAttributeDocument } from "@jasper/shared";
import { useOrderConfiguration } from "@jasper/use-cases";
import { Alert, Loader, Stack, Stepper } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useRef, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

/**
 * Reuses webapp v2 steps and the same route pattern
 * (/orders/:orderId/configure/products/:productId/:step?) so `useProductWorkflow`
 * resolves the current step. Unlike webapp there is no inter-product navigation:
 * completing the last step returns to the order edit page
 */

const LabsProductConfigureContent = ({
  orderId,
  productId,
  product,
  orderConfiguration,
}: {
  orderId: string;
  productId: string;
  product: ConfigurableProduct;
  orderConfiguration: OrderConfiguration;
}) => {
  const navigate = useNavigate();
  const [isNavigatingAfterSave, setIsNavigatingAfterSave] = useState(false);
  const stepSaveCoordinator = useStepSaveCoordinator();
  const bulkGroup = useBulkProductGroup({
    leaderProduct: product,
    products: orderConfiguration.order?.products ?? [],
  });
  const { bulkProductFollowerIds } = bulkGroup;

  const [refetchOrder] = useLazyQuery(GetOrderByUniqueAttributeDocument, {
    variables: { where: { id: orderId } },
    fetchPolicy: "network-only",
  });

  const backToOrder = async () => {
    await refetchOrder();
    navigate(`/orders/${orderId}/edit`);
  };

  const { currentStep, stepIndex, activeSteps, goNext, goBack, isFirst, goTo } =
    useProductWorkflow({
      steps: getStepsForProduct(product),
      orderId,
      productId,
      completedStepKeys: getCompletedSteps(product),
      onComplete: backToOrder,
      product,
      bulkProductIds: bulkProductFollowerIds,
    });

  const isMutationInFlight =
    orderConfiguration.isUpdatingProduct ||
    orderConfiguration.isUpdatingProducts ||
    orderConfiguration.isAddingProduct ||
    orderConfiguration.isAddingProducts ||
    orderConfiguration.isRemovingProducts;
  const isMutationInFlightRef = useRef(isMutationInFlight);
  useEffect(() => {
    isMutationInFlightRef.current = isMutationInFlight;
  }, [isMutationInFlight]);

  const itemType = getItemTypeForProductType(product.type);

  const onBack = isFirst ? backToOrder : goBack;

  const goNextWhenSaved = async () => {
    if (isNavigatingAfterSave) {
      return;
    }

    const getCurrentStepState = () =>
      stepSaveCoordinator
        .getStepStates()
        .find(state => state.stepKey === currentStep.key);

    setIsNavigatingAfterSave(true);
    try {
      const stepState = getCurrentStepState();
      if (stepState) {
        await stepState.validateAllFields();
        await stepSaveCoordinator.waitUntilIdle({
          isMutating: () => isMutationInFlightRef.current,
        });

        // Re-read the state: the getter returns a snapshot of the form state
        const validatedStepState = getCurrentStepState();
        if (validatedStepState?.isInvalid) {
          return;
        }

        await validatedStepState?.flush();
        await stepSaveCoordinator.waitUntilIdle({
          isMutating: () => isMutationInFlightRef.current,
        });
      }

      await goNext();
    } catch {
      notifications.show({
        title: "Saving in progress",
        message:
          "Saving is taking longer than expected. Please try again in a few moments.",
        color: "red",
      });
    } finally {
      setIsNavigatingAfterSave(false);
    }
  };

  const stepProps = {
    orderId,
    products: bulkGroup.configuredProducts,
    orderConfiguration,
    onNext: goNextWhenSaved,
    onBack,
    itemType,
    stepKey: currentStep.key,
    goToStep: () => goTo(currentStep.key),
    registerStepSave: stepSaveCoordinator.registerStepSave,
  };

  return (
    <Stack gap="md" h="100%" w="100%" p="md">
      <Stepper active={stepIndex} onStepClick={() => undefined}>
        {activeSteps.map(step => (
          <Stepper.Step key={step.key} label={step.label} />
        ))}
      </Stepper>
      <currentStep.Component key={currentStep.key} {...stepProps} />
      <StepNavigation
        onNext={goNextWhenSaved}
        onBack={onBack}
        isSubmitting={isNavigatingAfterSave || isMutationInFlight}
        nextLabel="Next"
        backLabel="Back"
      />
    </Stack>
  );
};

export const LabsProductConfigure = () => {
  const {
    orderId = "",
    productId = "",
    step,
  } = useParams<{ orderId: string; productId: string; step?: string }>();
  const { search } = useLocation();

  const orderConfiguration = useOrderConfiguration({ orderId });
  const { order } = orderConfiguration;
  const product = order?.products?.find(
    orderProduct => orderProduct?.id === productId
  );

  if (orderConfiguration.isLoading) {
    return (
      <Stack h="100%" w="100%" justify="center" align="center">
        <Loader />
      </Stack>
    );
  }

  if (!product) {
    return (
      <Stack p="md">
        <Alert color="red">Produit introuvable pour cette configuration</Alert>
      </Stack>
    );
  }

  if (!step) {
    const steps = getStepsForProduct(product);
    return (
      <Navigate
        to={`${buildProductStepPath(orderId, productId, steps[0].key)}${search}`}
        replace
      />
    );
  }

  return (
    <MantineJasperThemeProvider version="v2">
      <LabsProductConfigureContent
        orderId={orderId}
        productId={productId}
        product={product}
        orderConfiguration={orderConfiguration}
      />
    </MantineJasperThemeProvider>
  );
};

export default LabsProductConfigure;
